import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Select from '../components/Select';
import { PageHeading, NtIcon } from '../components/NotionUI';
import {
  getTicketProjects,
  getProjectMailbox,
  saveProjectMailbox,
  deleteProjectMailbox,
  testProjectMailbox,
  checkProjectMailbox,
  testMailboxFilters,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import '../components/kernelSwitch.css';
import { fmtDateTime } from '../utils/format';

const PRESETS = {
  google: { imap_host: 'imap.gmail.com', imap_port: 993, imap_encryption: 'ssl', smtp_host: 'smtp.gmail.com', smtp_port: 465, smtp_encryption: 'ssl' },
};

const EMPTY_FORM = {
  is_active: true,
  email_address: '',
  from_name: '',
  imap_host: '',
  imap_port: 993,
  imap_encryption: 'ssl',
  imap_username: '',
  imap_password: '',
  imap_folder: 'INBOX',
  smtp_host: '',
  smtp_port: 587,
  smtp_encryption: 'tls',
  smtp_same: true,
  smtp_username: '',
  smtp_password: '',
  skip_spam: true,
  mark_as_read: true,
  filter_allow_senders: '',
  filter_block_senders: '',
  filter_block_subject: '',
  filter_require_subject: '',
  filter_recipients: '',
};

const FILTER_LISTS = ['filter_allow_senders', 'filter_block_senders', 'filter_block_subject', 'filter_require_subject', 'filter_recipients'];
const EMPTY_SAMPLE = { sample_from: '', sample_subject: '', sample_to: '', sample_is_reply: false };

const OUTCOME_TONE = { ticket: 'ok', comment: 'ok', ignored: 'idle', failed: 'bad' };

const detectPreset = (m) => (m && m.imap_host === 'imap.gmail.com' ? 'google' : 'custom');

function fromMailbox(m) {
  return {
    ...EMPTY_FORM,
    is_active: m.is_active,
    email_address: m.email_address,
    from_name: m.from_name || '',
    imap_host: m.imap_host,
    imap_port: m.imap_port,
    imap_encryption: m.imap_encryption,
    imap_username: m.imap_username,
    imap_folder: m.imap_folder || 'INBOX',
    smtp_host: m.smtp_host,
    smtp_port: m.smtp_port,
    smtp_encryption: m.smtp_encryption,
    smtp_same: !m.smtp_username,
    smtp_username: m.smtp_username || '',
    skip_spam: m.skip_spam !== false,
    mark_as_read: m.mark_as_read !== false,
    ...Object.fromEntries(FILTER_LISTS.map((key) => [key, m[key] || ''])),
  };
}

/**
 * Casella email di UNA coda: le email ricevute su quell'indirizzo diventano
 * ticket (o risposte a un ticket aperto) e le risposte dello staff partono
 * dallo stesso indirizzo. Raggiungibile da "Code di assistenza".
 */
/** Sezione richiudibile della pagina: il titolo è un pulsante, il contenuto resta nel DOM (così la validazione del modulo lo vede) ma nascosto. */
function MbBlock({ id, color, icon, title, summary, open, onToggle, off = false, extra = null, children }) {
  return (
    <section className={'nf-block' + (off ? ' nf-block-off' : '')} style={{ '--q': color }} data-section={id}>
      <header className={'nf-head' + (open ? '' : ' mb-head-closed')}>
        <h3 className="mb-h">
          <button type="button" className="mb-toggle" aria-expanded={open} aria-controls={`mb-body-${id}`} onClick={onToggle}>
            <span className="au-event-icon" aria-hidden="true"><NtIcon name={icon} size={16} /></span>
            <span className="mb-toggle-title">{title}</span>
            {!open && <span className="mb-summary">{summary}</span>}
            <svg className={'mb-chevron' + (open ? ' mb-chevron-open' : '')} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </h3>
        {extra}
      </header>
      <div id={`mb-body-${id}`} hidden={!open}>
        {children}
      </div>
    </section>
  );
}

function TicketProjectMailbox({ user, onLogout }) {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = Number(id);

  const [project, setProject] = useState(null);
  const [mailbox, setMailbox] = useState(null);
  const [emails, setEmails] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [preset, setPreset] = useState('custom');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [sample, setSample] = useState(EMPTY_SAMPLE);
  const [filterResult, setFilterResult] = useState(null);
  const [filterTesting, setFilterTesting] = useState(false);
  const [checkNote, setCheckNote] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Le tre sezioni di collegamento partono chiuse, per lasciare spazio al resto della pagina.
  const [openSections, setOpenSections] = useState({ active: false, incoming: false, outgoing: false });
  const toggleSection = (key) => setOpenSections((o) => ({ ...o, [key]: !o[key] }));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [projects, data] = await Promise.all([getTicketProjects(), getProjectMailbox(projectId)]);
      const p = (projects.projects || []).find((proj) => proj.id === projectId);
      if (!p) {
        setError(t('tickets.projectNotFound'));
        return;
      }
      setProject(p);
      setMailbox(data.mailbox);
      setEmails(data.emails || []);
      if (data.mailbox) {
        setForm(fromMailbox(data.mailbox));
        setPreset(detectPreset(data.mailbox));
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setTestResult(null);
    setNotice('');
  };

  const onAddressChange = (value) => {
    // L'utente di accesso segue l'indirizzo finché non lo si cambia a mano.
    const followUser = form.imap_username === '' || form.imap_username === form.email_address;
    set({ email_address: value, ...(followUser ? { imap_username: value } : {}) });
  };

  const choosePreset = (value) => {
    setPreset(value);
    if (PRESETS[value]) {
      set({ ...PRESETS[value], imap_username: form.imap_username || form.email_address });
    }
  };

  const payload = () => ({
    is_active: form.is_active,
    email_address: form.email_address.trim(),
    from_name: form.from_name.trim(),
    imap_host: form.imap_host.trim(),
    imap_port: Number(form.imap_port),
    imap_encryption: form.imap_encryption,
    imap_username: form.imap_username.trim() || form.email_address.trim(),
    imap_password: form.imap_password,
    imap_folder: form.imap_folder.trim() || 'INBOX',
    smtp_host: form.smtp_host.trim(),
    smtp_port: Number(form.smtp_port),
    smtp_encryption: form.smtp_encryption,
    smtp_username: form.smtp_same ? '' : form.smtp_username.trim(),
    smtp_password: form.smtp_same ? '' : form.smtp_password,
    skip_spam: form.skip_spam,
    mark_as_read: form.mark_as_read,
    ...Object.fromEntries(FILTER_LISTS.map((key) => [key, form[key]])),
  });

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setTestResult(null);
    try {
      setTestResult(await testProjectMailbox(projectId, payload()));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  const updateSample = (patch) => {
    setSample((s) => ({ ...s, ...patch }));
    setFilterResult(null);
  };

  const handleFilterTest = async () => {
    setFilterTesting(true);
    setError('');
    setFilterResult(null);
    try {
      setFilterResult(await testMailboxFilters(projectId, { ...payload(), ...sample }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setFilterTesting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // Un campo obbligatorio vuoto in una sezione chiusa non si potrebbe raggiungere: si apre la
    // sezione e si mostra l'avviso del browser su quel campo.
    const formEl = e.currentTarget;
    if (!formEl.checkValidity()) {
      const key = formEl.querySelector(':invalid')?.closest('[data-section]')?.dataset.section;
      if (key) setOpenSections((o) => ({ ...o, [key]: true }));
      setTimeout(() => formEl.reportValidity(), 0);
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const data = await saveProjectMailbox(projectId, payload());
      setMailbox(data.mailbox);
      setForm(fromMailbox(data.mailbox));
      setNotice(t('mailbox.saved'));
      const fresh = await getProjectMailbox(projectId);
      setEmails(fresh.emails || []);
    } catch (err) {
      setError(errorMessage(err, t('mailbox.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(t('mailbox.removeConfirm'))) return;
    setError('');
    try {
      await deleteProjectMailbox(projectId);
      setMailbox(null);
      setEmails([]);
      setForm(EMPTY_FORM);
      setPreset('custom');
      setTestResult(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    setError('');
    setCheckNote('');
    try {
      const data = await checkProjectMailbox(projectId);
      setMailbox(data.mailbox);
      setEmails(data.emails || []);
      const r = data.result;
      setCheckNote(r.error
        ? `${t('mailbox.lastError')}: ${r.error}`
        : t('mailbox.checkResult', { tickets: r.imported, replies: r.replies, ignored: r.ignored, failed: r.failed }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  const encryptionOptions = [
    <option key="ssl" value="ssl">{t('mailbox.encSsl')}</option>,
    <option key="tls" value="tls">{t('mailbox.encTls')}</option>,
    <option key="none" value="none">{t('mailbox.encNone')}</option>,
  ];

  const input = (key, extra = {}) => (
    <input value={form[key]} onChange={(e) => set({ [key]: e.target.value })} {...extra} />
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="mail" title={t('mailbox.title')} subtitle={project ? (
            <span className="mb-queue" style={{ '--pill': project.color || 'var(--primary)' }}>
              <i className="mb-queue-dot" aria-hidden="true" />
              <span className="mb-queue-label">{t('mailbox.queue')}</span>
              <strong>{project.name}</strong>
            </span>
          ) : ''} />

        <div className="tp-toolbar">
          <button className="tpl-back fd-back" type="button" onClick={() => navigate('/ticket-projects')}>
            <NtIcon name="back" /> {t('tickets.backToQueues')}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {notice && <div className="mb-notice">{notice}</div>}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : (
          <form onSubmit={handleSave} className="nf-form mb-form" noValidate>
            <p className="tp-hint nf-hint">{t('mailbox.hint')}</p>

            <MbBlock
              id="active"
              color="#2563eb"
              icon="mail"
              title={t('mailbox.activeLabel')}
              summary={form.email_address || t('mailbox.notSet')}
              open={openSections.active}
              onToggle={() => toggleSection('active')}
              off={!form.is_active}
              extra={
                <button
                type="button"
                role="switch"
                aria-checked={form.is_active}
                className={'au-switch' + (form.is_active ? ' au-switch-on' : '')}
                onClick={() => set({ is_active: !form.is_active })}
                aria-label={t('mailbox.activeLabel')}
                >
                <span className="au-switch-knob" />
                </button>
              }
            >
              <div className="mb-grid">
                <p className="tp-hint mb-wide">{t('mailbox.activeHelp')}</p>
                <div className="form-group">
                  <label>{t('mailbox.address')}</label>
                  {input('email_address', { type: 'email', required: true, onChange: (e) => onAddressChange(e.target.value), placeholder: 'assistenza@azienda.it' })}
                  <small className="mb-help">{t('mailbox.addressHelp')}</small>
                </div>
                <div className="form-group">
                  <label>{t('mailbox.fromName')}</label>
                  {input('from_name', { placeholder: t('mailbox.fromNamePlaceholder') })}
                </div>
                <div className="form-group">
                  <label>{t('mailbox.presetLabel')}</label>
                  <Select value={preset} onChange={(e) => choosePreset(e.target.value)}>
                    <option value="custom">{t('mailbox.presetCustom')}</option>
                    <option value="google">{t('mailbox.presetGoogle')}</option>
                  </Select>
                </div>
                {preset === 'google' && <p className="mb-callout mb-wide">{t('mailbox.googleHelp')}</p>}
              </div>
            </MbBlock>

            <MbBlock
              id="incoming"
              color="#7c3aed"
              icon="arrowDown"
              title={t('mailbox.incoming')}
              summary={form.imap_host ? `${form.imap_host}:${form.imap_port}` : '' || t('mailbox.notSet')}
              open={openSections.incoming}
              onToggle={() => toggleSection('incoming')}
            >
              <div className="mb-grid">
                <div className="form-group">
                  <label>{t('mailbox.host')}</label>
                  {input('imap_host', { required: true, placeholder: 'imap.azienda.it' })}
                </div>
                <div className="form-group mb-narrow">
                  <label>{t('mailbox.port')}</label>
                  {input('imap_port', { type: 'number', min: 1, max: 65535, required: true })}
                </div>
                <div className="form-group">
                  <label>{t('mailbox.encryption')}</label>
                  <Select value={form.imap_encryption} onChange={(e) => set({ imap_encryption: e.target.value })}>{encryptionOptions}</Select>
                </div>
                <div className="form-group">
                  <label>{t('mailbox.username')}</label>
                  {input('imap_username', { placeholder: t('mailbox.usernamePlaceholder'), autoComplete: 'off' })}
                </div>
                <div className="form-group">
                  <label>{t('mailbox.password')}</label>
                  {input('imap_password', {
                    type: 'password',
                    autoComplete: 'new-password',
                    required: !mailbox,
                    placeholder: mailbox?.has_imap_password ? t('mailbox.passwordSet') : '',
                  })}
                </div>
                <div className="form-group">
                  <label>{t('mailbox.folder')}</label>
                  {input('imap_folder', { placeholder: 'INBOX' })}
                </div>
              </div>
            </MbBlock>

            <MbBlock
              id="outgoing"
              color="#16a34a"
              icon="arrowUp"
              title={t('mailbox.outgoing')}
              summary={form.smtp_host ? `${form.smtp_host}:${form.smtp_port}` : '' || t('mailbox.notSet')}
              open={openSections.outgoing}
              onToggle={() => toggleSection('outgoing')}
            >
              <div className="mb-grid">
                <div className="form-group">
                  <label>{t('mailbox.host')}</label>
                  {input('smtp_host', { required: true, placeholder: 'smtp.azienda.it' })}
                </div>
                <div className="form-group mb-narrow">
                  <label>{t('mailbox.port')}</label>
                  {input('smtp_port', { type: 'number', min: 1, max: 65535, required: true })}
                </div>
                <div className="form-group">
                  <label>{t('mailbox.encryption')}</label>
                  <Select value={form.smtp_encryption} onChange={(e) => set({ smtp_encryption: e.target.value })}>{encryptionOptions}</Select>
                </div>
                <label className="ksw-row mb-wide"><span className="ksw"><input type="checkbox" checked={form.smtp_same} onChange={(e) => set({ smtp_same: e.target.checked })} /><span className="ksw-slider" /></span><span>{t('mailbox.smtpSame')}</span></label>
                {!form.smtp_same && (
                  <>
                    <div className="form-group">
                      <label>{t('mailbox.smtpUsername')}</label>
                      {input('smtp_username', { autoComplete: 'off' })}
                    </div>
                    <div className="form-group">
                      <label>{t('mailbox.smtpPassword')}</label>
                      {input('smtp_password', {
                        type: 'password',
                        autoComplete: 'new-password',
                        placeholder: mailbox?.has_smtp_password ? t('mailbox.passwordSet') : '',
                      })}
                    </div>
                  </>
                )}
              </div>
            </MbBlock>

            <section className="nf-block" style={{ '--q': '#0891b2' }}>
              <header className="nf-head">
                <span className="au-event-icon" aria-hidden="true"><NtIcon name="eye" size={16} /></span>
                <h3>{t('mailbox.filtersTitle')}</h3>
              </header>
              <div className="mb-grid">
                <p className="tp-hint mb-wide">{t('mailbox.filtersHint')}</p>
                <label className="ksw-row mb-wide"><span className="ksw"><input type="checkbox" checked={form.skip_spam} onChange={(e) => set({ skip_spam: e.target.checked })} /><span className="ksw-slider" /></span><span><strong>{t('mailbox.skipSpam')}</strong> — {t('mailbox.skipSpamHelp')}</span></label>
                <label className="ksw-row mb-wide"><span className="ksw"><input type="checkbox" checked={form.mark_as_read} onChange={(e) => set({ mark_as_read: e.target.checked })} /><span className="ksw-slider" /></span><span><strong>{t('mailbox.markRead')}</strong> — {t('mailbox.markReadHelp')}</span></label>
                {FILTER_LISTS.map((key) => (
                  <div className="form-group mb-wide" key={key}>
                    <label>{t(`mailbox.${key}`)}</label>
                    <textarea
                      rows={3}
                      value={form[key]}
                      onChange={(e) => set({ [key]: e.target.value })}
                      placeholder={t(`mailbox.${key}_ph`)}
                    />
                    <small className="mb-help">{t(`mailbox.${key}_help`)}</small>
                  </div>
                ))}

                <div className="mb-sample mb-wide">
                  <div className="mb-sample-head">
                    <span className="mb-sample-icon" aria-hidden="true"><NtIcon name="search" size={18} /></span>
                    <div>
                      <h4>{t('mailbox.sampleTitle')}</h4>
                      <p>{t('mailbox.sampleHint')}</p>
                    </div>
                  </div>
                  <div className="mb-sample-grid">
                    <div className="form-group">
                      <label>{t('mailbox.sampleFrom')}</label>
                      <input type="email" value={sample.sample_from} onChange={(e) => updateSample({ sample_from: e.target.value })} placeholder={t('mailbox.sampleFromPh')} />
                    </div>
                    <div className="form-group">
                      <label>{t('mailbox.sampleSubject')}</label>
                      <input value={sample.sample_subject} onChange={(e) => updateSample({ sample_subject: e.target.value })} placeholder={t('mailbox.sampleSubjectPh')} />
                    </div>
                    <div className="form-group">
                      <label>{t('mailbox.sampleTo')}</label>
                      <input value={sample.sample_to} onChange={(e) => updateSample({ sample_to: e.target.value })} placeholder={t('mailbox.sampleToPh')} />
                    </div>
                  </div>
                  <div className="mb-sample-row">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={sample.sample_is_reply}
                      className="mb-sample-toggle"
                      onClick={() => updateSample({ sample_is_reply: !sample.sample_is_reply })}
                    >
                      <span className={'au-switch' + (sample.sample_is_reply ? ' au-switch-on' : '')} aria-hidden="true"><span className="au-switch-knob" /></span>
                      <span>{t('mailbox.sampleReply')}</span>
                    </button>
                    <button type="button" className="btn mb-sample-run" onClick={handleFilterTest} disabled={filterTesting || !sample.sample_from.trim()}>
                      <NtIcon name="search" size={14} /> {t('mailbox.sampleRun')}
                    </button>
                  </div>
                  {filterResult && (
                    <div className={'mb-verdict ' + (filterResult.accepted ? 'mb-verdict-ok' : 'mb-verdict-bad')} role="status">
                      <span className="mb-verdict-icon" aria-hidden="true">{filterResult.accepted ? '✓' : '✕'}</span>
                      <div>
                        <strong>{filterResult.accepted ? t('mailbox.sampleAccepted') : t('mailbox.sampleRejected')}</strong>
                        <span>{filterResult.accepted ? t('mailbox.sampleAcceptedHelp') : filterResult.reason}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Esito della prova e pulsanti restano insieme, fissi in fondo alla finestra mentre si scorre la pagina. */}
            <div className="mb-dock">
              {testResult && (
                <div className="mb-tests" role="status">
                  {['imap', 'smtp'].map((kind) => (
                    <div key={kind} className={'mb-test ' + (testResult[kind].ok ? 'mb-test-ok' : 'mb-test-bad')}>
                      <span aria-hidden="true">{testResult[kind].ok ? '✓' : '✕'}</span>
                      <span className="mb-test-text">
                        {testResult[kind].ok
                          ? t(kind === 'imap' ? 'mailbox.testOkImap' : 'mailbox.testOkSmtp')
                          : <><strong>{t(kind === 'imap' ? 'mailbox.testFailImap' : 'mailbox.testFailSmtp')}:</strong> {testResult[kind].error}</>}
                      </span>
                      {kind === 'smtp' && (
                        <button type="button" className="mb-test-close" onClick={() => setTestResult(null)} aria-label={t('mailbox.dismiss')} title={t('mailbox.dismiss')}>×</button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mb-savebar">
                {mailbox && (
                  <button type="button" className="btn btn-secondary mb-remove" onClick={handleRemove}>{t('mailbox.remove')}</button>
                )}
                <span className="mb-spacer" />
                <button type="button" className="btn btn-secondary" onClick={handleTest} disabled={testing}>
                  {testing ? t('mailbox.testing') : t('mailbox.test')}
                </button>
                <button type="submit" className="btn" disabled={saving}>
                  {saving ? t('common.saving') : t('mailbox.save')}
                </button>
              </div>
            </div>

            {mailbox && (
              <section className="nf-block" style={{ '--q': '#d97706' }}>
                <header className="nf-head">
                  <span className="au-event-icon" aria-hidden="true"><NtIcon name="clock" size={16} /></span>
                  <h3>{t('mailbox.statusTitle')}</h3>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleCheck} disabled={checking || !mailbox.is_active}>
                    {checking ? t('mailbox.checking') : t('mailbox.checkNow')}
                  </button>
                </header>
                <div className="mb-status">
                  <div><span className="mb-k">{t('mailbox.lastCheck')}</span> {mailbox.last_checked_at ? fmtDateTime(mailbox.last_checked_at) : t('mailbox.never')}</div>
                  {mailbox.last_error && <div className="mb-error"><span className="mb-k">{t('mailbox.lastError')}</span> {mailbox.last_error}</div>}
                  {checkNote && <div className="mb-note">{checkNote}</div>}

                  <h4 className="mb-sub">{t('mailbox.recent')}</h4>
                  {emails.length === 0 ? (
                    <p className="tp-hint">{t('mailbox.noEmails')}</p>
                  ) : (
                    <ul className="mb-list">
                      {emails.map((m) => (
                        <li key={m.id}>
                          <span className={`mt-status mt-status-${OUTCOME_TONE[m.outcome] || 'idle'}`}>{t(`mailbox.outcome_${m.outcome}`)}</span>
                          <span className="mb-mail">
                            <strong>{m.subject || '—'}</strong>
                            <small>{m.counterpart} · {fmtDateTime(m.created_at)}{m.detail ? ` · ${m.detail}` : ''}</small>
                          </span>
                          {m.ticket_id && <Link className="mb-ticket" to={`/tickets/${m.ticket_id}`} state={{ from: `/ticket-projects/${projectId}/mailbox` }}>{t('mailbox.openTicket', { id: m.ticket_id })}</Link>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            )}

            <section className="mb-how">
              <h4>{t('mailbox.howTitle')}</h4>
              <ul>
                <li>{t('mailbox.how1')}</li>
                <li>{t('mailbox.how2')}</li>
                <li>{t('mailbox.how3')}</li>
                <li>{t('mailbox.how4')}</li>
              </ul>
              <p><strong>{t('mailbox.cronTitle')}.</strong> {t('mailbox.cronHelp')}</p>
            </section>
          </form>
        )}
      </div>
    </Layout>
  );
}

export default TicketProjectMailbox;
